import React, { useEffect } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerHeader,
  DrawerOverlay,
  Button,
  Box,
  Text,
  Input,
  Select,
  useToast,
  VStack,
  HStack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  IconButton,
  Divider,
} from '@chakra-ui/react';
import { sumServiceEventsMinutes } from '../../utils/serviceDuration';
import ModelMultiSearchSelect from './ModelMultiSearchSelect';
import AdminDrawerContent from '../layout/AdminDrawerContent';
import { AdminField, useAdminFormFieldProps } from '../layout/AdminFormControls';
import { MdDelete, MdAdd } from 'react-icons/md';
import { getData } from '../../utils/httpClient';
import ProductSearchSelect from '../stock/ProductSearchSelect';
import useBcuUsdRate from '../../hooks/useBcuUsdRate';
import { formatMoneyDual, toUyuAmount } from '../../utils/currencyDisplay';
import { formatNumber } from '../../utils/transform';

const sortServiceEventsByCode = (serviceEvents = []) =>
  [...serviceEvents].sort((a, b) =>
    String(a?.event?.code ?? '').localeCompare(String(b?.event?.code ?? ''), 'es', {
      numeric: true,
    }),
  );

const ServicesEditModal = ({
  isOpen,
  onClose,
  service,
  isNew,
  onCreate,
  onUpdate,
}) => {
  const [formData, setFormData] = React.useState({});
  const [events, setEvents] = React.useState([]);
  const [brandsList, setBrandsList] = React.useState([]);
  const [modelsList, setModelsList] = React.useState([]);
  const [selectedModelIds, setSelectedModelIds] = React.useState([]);
  const [laborCost, setLaborCost] = React.useState(0);

  const [estimatedTime, setEstimatedTime] = React.useState('');
  const [selectedEventId, setSelectedEventId] = React.useState('');
  const [estimatedCost, setEstimatedCost] = React.useState(0);

  const [recommendedProducts, setRecommendedProducts] = React.useState([]);
  const [selectedProductId, setSelectedProductId] = React.useState('');
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [quantity, setQuantity] = React.useState('');
  const { rate: usdRate } = useBcuUsdRate();
  

  const toast = useToast();

  const { fieldProps, selectProps } = useAdminFormFieldProps();

  // Get data from API

  const getEvents = async () => {
    try {
      const response = await getData('/events');
      if (response?.payload?.events) setEvents(response.payload.events);
    } catch (error) {
      toast({
        title: 'Error al obtener eventos',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getModelsByBrand = async (brandId) => {
    if (!brandId) {
      setModelsList([]);
      return;
    }
    try {
      const response = await getData(`/models?brandId=${brandId}`);
      setModelsList(
        [...(response?.payload?.models || [])].sort((a, b) =>
          String(a?.description || '').localeCompare(
            String(b?.description || ''),
            'es',
            { sensitivity: 'base' },
          ),
        ),
      );
    } catch (error) {
      setModelsList([]);
    }
  };

  const getBrands = async () => {
    try {
      const response = await getData('/brands');
      if (response?.payload?.brands) {
        setBrandsList(response.payload.brands);
      }
    } catch (error) {
      setBrandsList([]);
      toast({
        title: 'Error al obtener marcas',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const applyServiceToForm = (svc) => {
    if (!svc) return;

    setFormData({
      id: svc?.id ?? null,
      name: svc?.name ?? '',
      brandId: svc?.brandId ?? 0,
      createdAt: svc?.createdAt ?? '',
      updatedAt: svc?.updatedAt ?? '',
      servicesEvents: sortServiceEventsByCode(
        (svc?.servicesEvents ?? []).map((serviceEvent) => ({
          ...serviceEvent,
        })),
      ),
      volkswagenCost: svc?.volkswagenCost ?? 0,
      serviceTypeId: svc?.serviceTypeId ?? 1,
      enabled: svc?.enabled ?? true,
    });

    setRecommendedProducts(svc?.recommendedProducts ?? []);
    setSelectedProductId('');
    setSelectedProduct(null);

    setSelectedModelIds(
      (svc?.serviceModels || [])
        .map((sm) => sm.modelId ?? sm.model?.id)
        .filter(Boolean)
        .map(Number),
    );

    if (svc?.brandId) {
      getModelsByBrand(svc.brandId);
    }
  };

  const getLaborCostHourly = async () => {
    try {
      const response = await getData('/services/labor-cost-hourly', {
        skipLoader: true,
      });
      const value = Number(response?.payload?.laborCostHourly);
      setLaborCost(Number.isFinite(value) ? value : 0);
    } catch (error) {
      setLaborCost(0);
      toast({
        title: 'Error al obtener costo de mano de obra',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    getEvents();
    getBrands();
    getLaborCostHourly();

    const loadService = async () => {
      let svc = service;
      if (service?.id && !isNew) {
        try {
          const response = await getData(`/services/${service.id}`, {
            skipLoader: true,
          });
          svc = response?.payload?.service ?? response ?? service;
        } catch (error) {
          toast({
            title: 'Error al cargar el servicio',
            description: error.message || 'Ocurrió un error inesperado.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      }
      applyServiceToForm(svc);
    };

    loadService();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, service?.id, isNew]);

  useEffect(() => {
    if (formData.brandId) {
      getModelsByBrand(Number(formData.brandId));
    }
  }, [formData.brandId]);


  useEffect(() => {
    calculateEstimatedCost();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, recommendedProducts, laborCost, usdRate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Usar una expresión condicional para asignar directamente el valor adecuado
    const formattedValue =
      name === 'brandId' || name === 'volkswagenCost'
        ? value === ''
          ? '0'
          : String(Number(value))
        : value;

    if (name === 'brandId') {
      setSelectedModelIds([]);
    }

    setFormData({
      ...formData,
      [name]: formattedValue,
    });
  };
  

  const handleAddEvent = () => {
    if (selectedEventId === '') {
      toast({
        title: 'Por favor, selecciona un evento.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const selectedEvent = events.find(
      (event) => event.id === Number(selectedEventId),
    );

    const existingEvent = formData.servicesEvents.find(
      (event) => event.eventId === Number(selectedEventId),
    );

    if (existingEvent) {
      toast({
        title: 'El evento ya exite.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } else {
      const newServiceEvent = {
        id: '',
        serviceId: formData.id,
        eventId: selectedEvent.id,
        estimatedTime: Number(selectedEvent.time) || Number(estimatedTime) || 0,
        event: selectedEvent,
      };

      setFormData({
        ...formData,
        servicesEvents: sortServiceEventsByCode([
          ...formData.servicesEvents,
          newServiceEvent,
        ]),
      });
      toast({
        title: 'Evento agregado al servicio.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
    setEstimatedTime('');
    setSelectedEventId('');
  };

  

  const handleEstimatedTimeChange = (e, event) => {
    const newEstimatedTime = parseInt(e.target.value || '0', 10);
    const updatedEvents = formData.servicesEvents.map((ev) =>
      ev.id === event.id
        ? {
            ...ev,
            estimatedTime: newEstimatedTime,
          }
        : ev,
    );
    setFormData({
      ...formData,
      servicesEvents: updatedEvents,
    });
  };

  const calculateLaborTime = () =>
    sumServiceEventsMinutes(formData?.servicesEvents || []);

  const calculateEstimatedCost = () => {
    let total = 0;

    try {
      const laborTime = calculateLaborTime();

      // Calculate total by combining service labor cost and events cost
      total = (((laborCost * (laborTime / 60))) + calculateEventEstimatedCost()).toFixed(2);

    } catch (e) {
      total = 0;
    }

    setEstimatedCost(total);
  };

  const calculateEventEstimatedCost = () => {
    const recommendedProductsCost = recommendedProducts?.reduce(
      (total, recommendedProduct) => {
        const productCost =
          toUyuAmount(
            recommendedProduct.product.currentCost,
            recommendedProduct.product.currency?.code,
            usdRate || recommendedProduct.product.currency?.rate,
          ) ?? 0;
        return total + productCost * recommendedProduct.quantity;
      },
      0,
    );

    return recommendedProductsCost;
  };

  const handleSave = () => {

    const formattedData = {
      ...formData,
      recommendedProducts,
      modelIds: selectedModelIds,
      brandId: Number(formData.brandId), // Convierte brandId a número
      volkswagenCost: Number(formData.volkswagenCost), // Convierte volkswagenCost a número
      serviceTypeId: Number(formData.serviceTypeId || 1), // Asegurar que sea un número y por defecto 1
      enabled: Boolean(formData.enabled !== undefined ? formData.enabled : 1), // Convertir a boolean, por defecto true
    };

    if (isNew) {
      onCreate(formattedData);
    } else {
      onUpdate(formattedData);
    }
    toast({
      title: isNew ?'Servicio creado.' : 'Servicio actualizado.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    onClose();
  };


  const handleAddProduct = () => {
    if (!selectedProduct) {
      toast({
        title: 'Por favor, selecciona un producto.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const quantityValue = parseFloat(quantity);
    if (isNaN(quantityValue) || quantityValue < 1) {
      toast({
        title: 'Por favor, ingresa una cantidad válida.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const existingProduct = recommendedProducts.find(
      (recommended) => recommended.productId === selectedProduct.id
    );

    if (existingProduct) {
      const updatedRecommendedProducts = recommendedProducts.map((recommended) => {
        if (recommended.productId === selectedProduct.id) {
          return {
            ...recommended,
            quantity: (recommended.quantity || 0) + quantityValue,
            updatedAt: new Date().toISOString(),
          };
        }
        return recommended;
      });
      setRecommendedProducts(updatedRecommendedProducts);
    } else {
      const newRecommendedProduct = {
        serviceId: service?.id,
        productId: selectedProduct.id,
        product: selectedProduct,
        quantity: quantityValue,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setRecommendedProducts([...recommendedProducts, newRecommendedProduct]);
    }

    setSelectedProductId('');
    setSelectedProduct(null);
    setQuantity('');
    toast({
      title: 'Producto agregado a la lista de recomendados.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} closeOnOverlayClick={false}>
      <DrawerOverlay />
      <AdminDrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="lg" fontWeight="bold" textAlign="center">
          {isNew ? 'Nuevo Servicio' : 'Editar Servicio'}
        </DrawerHeader>
        <DrawerBody>
          <VStack spacing={4} align="stretch">
            <AdminField label="Nombre del servicio" isRequired>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Nombre del servicio"
                {...fieldProps}
              />
            </AdminField>
            <AdminField label="Costo de mano de obra (por hora - UYU)">
              <Input
                name="laborCost"
                type="number"
                value={laborCost}
                placeholder="Costo de Mano de Obra"
                readOnly
                {...fieldProps}
              />
            </AdminField>
            <AdminField label="Marca">
              <Select
                name="brandId"
                value={formData.brandId}
                onChange={handleChange}
                {...selectProps}
              >
              <option value={0} disabled>
                Seleccionar marca
              </option>
              {brandsList?.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
              </Select>
            </AdminField>
            {!formData.brandId ? (
              <>
                <Text fontWeight="bold" pt={2}>
                  Modelos de vehículo
                </Text>
                <Text fontSize="sm" color="orange.600">
                  Seleccione una marca para buscar y asignar modelos.
                </Text>
              </>
            ) : (
              <ModelMultiSearchSelect
                models={[
                  ...modelsList,
                  ...(service?.serviceModels || [])
                    .map((sm) => sm.model)
                    .filter(Boolean)
                    .filter(
                      (m) => !modelsList.some((x) => Number(x.id) === Number(m.id)),
                    ),
                ]}
                selectedIds={selectedModelIds}
                onChange={setSelectedModelIds}
                placeholder="Buscar modelo por nombre…"
                hint="Use el tag «Todos los modelos» o deje la lista vacía para que el servicio aplique a cualquier vehículo de la marca."
              />
            )}
            <Text fontWeight="bold" mt={3} color="blue.700">
              Tiempo total estimado: {calculateLaborTime()} min
            </Text>
            <Divider pt={5} pb={5} />
            <Text fontWeight="bold" pt={5}>
              Agregar Evento
            </Text>
            <VStack spacing={4} align="stretch">
              <Select
                value={selectedEventId}
                onChange={(e) => { 
                  setSelectedEventId(e.target.value);
                  setEstimatedTime(events.find((event) => event.id === Number(e.target.value))?.time || '');}}
                placeholder="Seleccionar evento"
              >
                {events
                  ?.sort((a, b) => (a.code || '').localeCompare(b.code || '', 'es'))
                  .map((event) => (
                    <option key={event.id} value={event.id}>
                      {event?.code} - {event?.name}
                    </option>
                  ))}
              </Select>

              <HStack align="center">
                <Text fontWeight="bold" w="30%">
                  Tiempo estimado (min)
                </Text>
                <Input
                  type="number"
                  value={estimatedTime === '' ? '0' : estimatedTime} // Default to "0" if empty
                  onChange={(e) =>
                    setEstimatedTime(
                      e.target.value === ''
                        ? '0'
                        : String(Number(e.target.value)),
                    )
                  }
                  placeholder="Tiempo estimado"
                  min={1}
                  flex="1"
                />
              </HStack>

              <IconButton
                icon={<MdAdd />}
                colorScheme="gray"
                onClick={handleAddEvent}
                aria-label="Agregar evento"
                alignSelf="flex-end"
              />
            </VStack>
            <Text fontWeight="bold">Eventos Asociados</Text>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Código</Th>
                  <Th>Evento</Th>
                  <Th>Tiempo estimado</Th>
                  <Th>Acciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {formData?.servicesEvents?.map((event) => (
                  <Tr key={event.eventId}>
                    <Td>{event.event?.code}</Td>
                    <Td>{event.event?.name}</Td>
                    <Td>
                      <Input
                        value={event.estimatedTime}
                        onChange={(e) => handleEstimatedTimeChange(e, event)}
                        size="sm"
                        width="80px"
                      />
                    </Td>
                    <Td>
                      <IconButton
                        icon={<MdDelete />}
                        bg="white"
                        color="red.500"
                        size="lg"
                        fontSize="24px"
                        aria-label="Eliminar evento"
                        _hover={{ bg: 'gray.200' }}
                        onClick={() => {
                          const updatedEvents = formData.servicesEvents.filter(
                            (ev) => ev.eventId !== event.eventId,
                          );
                          setFormData({
                            ...formData,
                            servicesEvents: updatedEvents,
                          });
                          toast({
                            title: 'Evento eliminado de la lista.',
                            status: 'success',
                            duration: 3000,
                            isClosable: true,
                          });
                        }}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Divider pt={5} pb={5} />
            <Text pt={5} fontWeight="bold">Agregar Productos Recomendados</Text>
              <HStack align="flex-end" spacing={3}>
                <Box flex={1}>
                  <ProductSearchSelect
                    label={null}
                    value={selectedProductId}
                    onChange={(id, product) => {
                      setSelectedProductId(id || '');
                      setSelectedProduct(product || null);
                    }}
                    placeholder="Buscar por N° de pieza o nombre…"
                  />
                </Box>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="Cantidad"
                  min={1}
                  w="110px"
                  mb={3}
                />
                <IconButton
                  icon={<MdAdd />}
                  colorScheme="gray"
                  onClick={handleAddProduct}
                  aria-label="Agregar producto"
                  mb={3}
                />
              </HStack>
              <Text fontWeight="bold">Productos Recomendados</Text>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Código</Th>
                    <Th>Producto</Th>
                    <Th>Moneda</Th>
                    <Th>Costo Actual</Th>
                    <Th>Cantidad</Th>
                    <Th>Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {recommendedProducts.map((recommended) => (
                    <Tr key={recommended.productId}>
                      <Td>{recommended.product.code}</Td>
                      <Td>{recommended.product.name}</Td>
                      <Td>{recommended.product.currency?.code || 'UYU'}</Td>
                      <Td>
                        {formatMoneyDual(
                          recommended.product.currentCost,
                          recommended.product.currency?.code,
                          usdRate || recommended.product.currency?.rate,
                        )}
                      </Td>
                      <Td>{recommended.quantity || 0}</Td>
                      <Td>
                        <IconButton
                          icon={<MdDelete />}
                          bg="white"
                          color="red.500"
                          size="lg" // Aumentar tamaño del icono
                          fontSize="24px" // Cambiar tamaño del icono
                          aria-label="Eliminar producto"
                          _hover={{ bg: 'gray.200' }}
                          onClick={() => {
                            setRecommendedProducts(
                              recommendedProducts.filter(
                                (rp) => rp.productId !== recommended.productId,
                              ),
                            );
                            toast({
                              title:
                                'Producto eliminado de la lista de recomendados.',
                              status: 'success',
                              duration: 3000,
                              isClosable: true,
                            });
                          }}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            <Divider pt={5} />

            <AdminField label="Costo estimado total (UYU)">
              <Input
                value={
                  estimatedCost != null && estimatedCost !== ''
                    ? formatNumber(estimatedCost)
                    : ''
                }
                readOnly
                placeholder="Costo Estimado"
                {...fieldProps}
              />
            </AdminField>
            <AdminField label="Costo Volkswagen">
              <Input
                name="volkswagenCost"
                onChange={handleChange}
                value={formData.volkswagenCost === '' ? '0' : formData.volkswagenCost}
                placeholder="Costo Volkswagen"
                type="number"
                step="0.01"
                {...fieldProps}
              />
            </AdminField>
          </VStack>
        </DrawerBody>
        <HStack justify="flex-end" p={4}>
          <Button variant="adminPrimary" onClick={handleSave}>
            Guardar
          </Button>
        </HStack>
      </AdminDrawerContent>
    </Drawer>
  );
};

export default ServicesEditModal;
